import { useTheme, Button, TextInput } from "@monolith-forensics/monolith-ui";
import { useEffect, useState, useRef } from "react";
import { useSnackbar } from "notistack";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Loader from "../../components/Loader";
import { usePermissions } from "../../hooks/usePermissions";
import styled from "styled-components";
import CaseStatusApi from "@/api/case-status";
import {
  CreateCaseStatusModal,
  DeleteCaseStatusModal,
} from "@/Features/Settings/Modals";
import { EditIcon } from "lucide-react";

interface CaseStatus {
  case_status_id: number;
  status_name: string;
  total: number;
}

const CaseStatusList = styled(
  ({
    className,
    caseStatuses,
    isLoading,
    setDeleteSelection,
    onDeleteClick,
  }: {
    className: string;
    caseStatuses: CaseStatus[];
    isLoading: boolean;
    setDeleteSelection: React.Dispatch<React.SetStateAction<CaseStatus | null>>;
    onDeleteClick: () => void;
  }) => {
    return (
      <div className={className}>
        {isLoading && <Loader />}
        {!isLoading &&
          caseStatuses.map((status) => {
            return (
              <CaseStatusItem
                key={status.case_status_id}
                data={status}
                setDeleteSelection={setDeleteSelection}
                onDeleteClick={onDeleteClick}
                allowEdit={
                  status.status_name !== "Active" &&
                  status.status_name !== "Closed"
                }
              />
            );
          })}
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const StatusItemWrapper = styled.div<{ hoverColor: string }>`
  display: flex;
  align-items: center;
  padding: 10px 10px;
  border-radius: 4px;
  &:hover {
    background-color: ${(props) => props.hoverColor};
  }
`;

const CaseStatusItem = ({
  data,
  setDeleteSelection,
  onDeleteClick,
  allowEdit,
}: {
  data: CaseStatus;
  setDeleteSelection: React.Dispatch<React.SetStateAction<CaseStatus | null>>;
  onDeleteClick: () => void;
  allowEdit: boolean;
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(data.status_name);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateMutation = useMutation({
    mutationFn: (params: { id: number; values: { status_name: string } }) =>
      CaseStatusApi.updateCaseStatus(params.id, params.values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-statuses", "list"] });
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while updating this case status. Please try again.",
        { variant: "error" }
      );
      setEditValue(data.status_name);
    },
  });

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEditClick = () => {
    if (allowEdit) {
      setIsEditing(true);
      setEditValue(data.status_name);
    }
  };

  const handleSave = async () => {
    if (editValue.trim() === "") {
      setEditValue(data.status_name);
    } else if (editValue !== data.status_name) {
      await updateMutation.mutateAsync({
        id: data.case_status_id,
        values: { status_name: editValue },
      });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(data.status_name);
    setIsEditing(false);
  };

  const handleDelete = () => {
    setDeleteSelection(data);
    onDeleteClick();
  };

  return (
    <StatusItemWrapper hoverColor={theme.palette.action.hover}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {!isEditing ? (
            <>
              <div
                style={{
                  fontSize: "larger",
                  padding: "2px",
                  cursor: allowEdit ? "text" : "default",
                }}
                onClick={handleEditClick}
              >
                {data.status_name}
              </div>
              {allowEdit && (
                <EditIcon
                  size={14}
                  color={theme.palette.primary.main}
                  style={{ cursor: "pointer" }}
                  onClick={handleEditClick}
                />
              )}
            </>
          ) : (
            <>
              <TextInput
                ref={inputRef}
                size="md"
                variant="outlined"
                value={editValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditValue(e.target.value)
                }
                style={{ flex: 1 }}
              />
              <Button
                size="xs"
                variant="contained"
                color="primary"
                onClick={handleSave}
              >
                Save
              </Button>
              <Button size="xs" variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          )}
        </div>
        <div style={{ color: theme.palette.text.secondary, padding: "2px" }}>
          {data.total} Cases
        </div>
      </div>
      {allowEdit && (
        <div style={{ marginLeft: "auto" }}>
          <Button
            variant="subtle"
            color="red"
            size="xs"
            disabled={!hasPermission()}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      )}
    </StatusItemWrapper>
  );
};

const CaseStatusSettings = styled(({ className }) => {
  const queryClient = useQueryClient();
  const [deleteSelection, setDeleteSelection] = useState<CaseStatus | null>(
    null
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: caseStatuses = [], isLoading } = useQuery({
    queryKey: ["case-statuses", "list"],
    queryFn: () => CaseStatusApi.getCaseStatuses(),
  });

  const handleCreateSubmit = () => {
    queryClient.invalidateQueries({ queryKey: ["case-statuses", "list"] });
  };

  const handleDelete = () => {
    queryClient.invalidateQueries({ queryKey: ["case-statuses", "list"] });
    setDeleteSelection(null);
  };

  return (
    <div className={className} style={{ maxWidth: 800, padding: "0px 30px" }}>
      <div>
        Create, delete, and edit case statuses. Create case statuses that will
        help identify the current state of a case. The "Active" and "Closed"
        options are permanent and cannot be changed.
      </div>
      <div style={{ margin: "25px 0px" }}>
        <Button
          variant="contained"
          color="primary"
          size="xs"
          onClick={() => setShowCreateModal(true)}
        >
          + Create Case Status
        </Button>
      </div>
      <CaseStatusList
        className=""
        caseStatuses={caseStatuses}
        isLoading={isLoading}
        setDeleteSelection={setDeleteSelection}
        onDeleteClick={() => setShowDeleteModal(true)}
      />
      <CreateCaseStatusModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
      />
      {caseStatuses && (
        <DeleteCaseStatusModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          deleteSelection={deleteSelection}
          caseStatuses={caseStatuses}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0px;
`;

export default CaseStatusSettings;
